import { doc, getDoc, runTransaction, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Customer numbers are "N" + an integer, e.g. "N10432".
//
// They USED to be `N${Math.floor(1000 + Math.random() * 9000)}` — a random
// 4-digit number. The ~9,000 imported customers already occupy N1001 upward, so
// almost every random pick duplicated an existing customer's number, and the
// number bore no relation to when the customer was created.
//
// Now they come from a counter document (`counters/customerCounter`), advanced
// inside a Firestore transaction exactly like generateDocNumber, so two clients
// creating customers at once can never get the same number.

// Numeric part of a customer number ("N10432" → 10432), or 0 if it has none.
export function customerNumberValue(customerNumber) {
  const m = /^N(\d+)$/i.exec(String(customerNumber || '').trim());
  return m ? parseInt(m[1], 10) : 0;
}

// Highest numeric customer number in a list of customers.
function maxCustomerNumber(customers) {
  return (customers || []).reduce((max, c) => Math.max(max, customerNumberValue(c.customerNumber)), 0);
}

// Reserve the next customer number.
//
// `knownCustomers` is the loaded customers array (from DataContext). It is a
// floor, not the source: the next number is always above BOTH the counter and
// every number in that list. That seeds the counter the first time it is used
// (it does not exist yet), and keeps it ahead of any number handed out by the
// old random code in a tab that has not reloaded. If the list is empty (not
// loaded yet) and the counter does not exist, the floor is read from Firestore
// once, so the first number can never collide with an existing customer.
export async function reserveCustomerNumber(knownCustomers) {
  const counterRef = doc(db, 'counters', 'customerCounter');
  let floor = maxCustomerNumber(knownCustomers);
  if (floor === 0 && !(await getDoc(counterRef)).exists()) {
    // First use with no customers loaded: find the true maximum once. Done
    // outside the transaction so a contention retry never repeats it.
    const snap = await getDocs(collection(db, 'customers'));
    floor = maxCustomerNumber(snap.docs.map(d => d.data()));
  }

  const next = await runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(counterRef);
    const count = counterSnap.exists() ? (counterSnap.data().count || 0) : 0;
    const value = Math.max(count, floor) + 1;
    transaction.set(counterRef, { count: value });
    return value;
  });

  return `N${next}`;
}
